/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Ganturin Vladimir <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {HttpParams} from '@angular/common/http';
import {inject, Injectable, signal} from '@angular/core';
import {FormlyFieldConfig} from '@ngx-formly/core';
import {CompoApiService, Environment, Feature, FeatureFilter, FeatureTypes, StoreService, translations} from 'compob2b-libs';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {TuiDay, TuiDayRange} from "@taiga-ui/cdk";
import {ActivatedRoute} from "@angular/router";
import {FormControl} from "@angular/forms";

@Injectable({
  providedIn: 'root'
})
export class ListFilterService {
  features: Feature[];
  fieldsFeatures$: BehaviorSubject<FormlyFieldConfig[]>;
  ctnActiveFeatures$: BehaviorSubject<number>;
  modelFeatures: any = {};
  modelFeaturesArr: any = {};
  yesNoSelectFilterKeys = ['enabled', 'approved', 'deleted'];
  skipFields = [];
  textFields = ['header', 'barCode', 'articul', 'articuls', 'syncUid'];
  /**
   * Параметры, которые переносятся из URL «как есть», потому что своего контрола в
   * форме у них нет: пагинация, сортировка, регион, поисковая строка.
   *
   * `hasStock` отсюда УБРАН: у него контрол есть (чекбокс «В наличии»), а этот список
   * перезаписывает результат разбора модели — то есть снятую галочку он возвращал из
   * URL обратно, и выключить фильтр было нельзя вовсе. Правило: параметр с контролом
   * сюда попадать не должен, иначе список спорит с пользователем.
   */
  passParams = ['catalog_id', 'keyword', 'region', 'order', 'direction', 'page', 'limit'];
  selectFields = ['classId'];
  route = inject(ActivatedRoute);
  fieldsFeatures: FormlyFieldConfig[] = [];

  // Открыта ли глобальная фильтр-панель. Пишется из layout.toggleFilter.
  // filter.component грузит getProductFilter лениво — только когда панель
  // открыта или есть catalog_id в URL (см. filter.component).
  isPanelOpen = signal(false);

  environment = inject(Environment);

  constructor(readonly compoApiService: CompoApiService, readonly storeService: StoreService) {
    this.fieldsFeatures$ = new BehaviorSubject<FormlyFieldConfig[]>([]);
    this.ctnActiveFeatures$ = new BehaviorSubject<number>(0);
  }

  getProductFilter(uri: string, id: number, params: HttpParams): Observable<any> {
    return this.compoApiService
      .getWithParams<FeatureFilter>(uri + '/get-filter-fields/' + id, params)
      .pipe(map(response => ({
        // Форма заполняется из АКТУАЛЬНОГО адреса, а не из `params`, с которыми ушёл
        // запрос. Это разные моменты времени, и разница видна на глаз: пока летит
        // запрос, человек успевает щёлкнуть ещё одну галочку и адрес меняется. Ответ
        // же приходит с параметрами СВОЕГО запроса — засеяв ими форму, мы бы стёрли
        // эту галочку и откатили URL. Воспроизведено в браузере на стенде.
        //
        // В сам запрос параметры по-прежнему идут замороженными: там наоборот важно,
        // чтобы фасеты соответствовали тому, что спрашивали.
        fieldsFeatures: this.configFields(response.data, this.currentQueryParams()),
        templateAggr: response.data.templateAggr,
      }),));
  }

  /** Текущий query адресной строки как `HttpParams` — состояние на момент ОТВЕТА. */
  private currentQueryParams(): HttpParams {
    let out = new HttpParams();
    const current = this.route.snapshot.queryParams ?? {};

    for (const [key, value] of Object.entries(current)) {
      if (value !== null && value !== undefined && value !== '') {
        out = out.set(key, String(value));
      }
    }
    return out;
  }

  getCtnActiveFeatures(): Observable<number> {
    return this.ctnActiveFeatures$.asObservable();
  }

  getFields(): Observable<FormlyFieldConfig[]> {
    return this.fieldsFeatures$.asObservable();
  }

  setFields(fields: FormlyFieldConfig[]) {
    this.fieldsFeatures$.next(fields);

    let cntActiveFeatures = fields?.filter(item => !this.isEmptyField(item))?.length;

    this.ctnActiveFeatures$.next(cntActiveFeatures);
  }

  isEmptyField(item: FormlyFieldConfig): boolean {
    if (item.fieldGroup) {
      if ((item.fieldGroup[0].defaultValue !== undefined && item.fieldGroup[0].defaultValue.length > 0) || (item.fieldGroup[1].defaultValue !== undefined && item.fieldGroup[1].defaultValue.length > 0)) {
        return false;
      }
    } else if (item.defaultValue != undefined && item.defaultValue != false) {
      return false;
    }

    return true;
  }

  configFields(filter: FeatureFilter, params: HttpParams): FormlyFieldConfig[] {
    if (this.skipFields.length > 0) {
      filter.featureProduct = filter.featureProduct.filter(element => !this.skipFields.includes(element.code));
      filter.features = filter.features.filter(element => !this.skipFields.includes(element.id))
    }

    this.fieldsFeatures = [];
    this.seedModelFromParams(params);

    filter.featureProduct?.forEach(element => {
      element.id = element.code;

      if (element.type == 'input' || element.type == 'textarea') {
        this.addTextInput(element, params);
      } else if (this.yesNoSelectFilterKeys.includes(element.code)) {
        this.addYesNoSelect(element, params);
      } else if (element.type === 'checkbox') {
        this.addCheckboxField(element, params);
      } else if (element.type === 'dateRange') {
        this.addDateRange(element, params);
      } else if (element.type === 'multiselect') {
        this.addMultiSelect(element, filter, params);
      } else if (element.type === 'select') {
        this.addSelect(element, params);
      } else if (element.type === 'select-custom') {
        this.addSimpleSelect(element, params);
      } else if (element.id) {
        console.error('CANT FIND featureProduct', element);
      }
    });

    filter.features?.forEach(item => {
      item.code = item.id;

      if (this.addExtendedFeature(item, params, filter)) {
        return;
      }
      if (item.featureTypeId === FeatureTypes.BOOLEAN) {
        this.addSelect(item, params);
      } else if (item.featureTypeId === FeatureTypes.INTERVAL || item.featureTypeId === FeatureTypes.RANGE) {
        this.addIntervalField(item, params, filter);
      } else if (item.featureTypeId === FeatureTypes.SELECT) {
        this.addSelect(item, params);
      } else if (!item.featureTypeId || item.featureTypeId === FeatureTypes.SELECT || item.featureTypeId === FeatureTypes.CHECKBOX) {
        this.addMultiSelect(item, filter, params);
      } else if (item.featureTypeId === FeatureTypes.DATE) {
        this.addDateRange(item, params);
      } else {
        console.error('CANT FIND features', item);
      }
    });

    this.setFields(this.fieldsFeatures);

    this.modelFeatures = {};

    return this.fieldsFeatures;
  }

  /**
   * Точка расширения раскладки `features` по типам: наследник, чей эндпоинт отдаёт
   * дополнительные типы (админский листинг PIM — `ProductFilterService`), строит контрол
   * сам и возвращает `true`. База ничего не добавляет — DAM, витрина и customer видят
   * прежние контролы.
   */
  protected addExtendedFeature(item, params: HttpParams, filter: FeatureFilter): boolean {
    return false;
  }

  addMultiSelect(item, filter: FeatureFilter, params) {
    const optionsList = this.compoApiService
      .naturalSort(item.values || item.featureValue, 'value')
      .map(value => {
        let cntInAggegation = 0;
        if (filter.featureAndValuesAggr && filter.featureAndValuesAggr[item.id] && filter.featureAndValuesAggr[item.id][value.id]) {
          cntInAggegation = filter.featureAndValuesAggr[item.id][value.id];
        }
        return {
          value: value.id,
          count: cntInAggegation,
          label: value.value + (cntInAggegation > 0 ? ` (${cntInAggegation})` : ''),
          // прокидываем мета-поля тегов, чтобы UI мог рисовать иконку/цвет рядом с опцией
          icon: value.icon ?? undefined,
          color: value.color ?? undefined,
        };
      });

    if (optionsList.length > 1) {
      let defaultValue = this.getMultiSelectDefaultValue(params, item, optionsList);

      const fieldConfig: FormlyFieldConfig = {
        key: item.id,
        type: 'select-multi-filter-property',
        defaultValue: defaultValue,
        templateOptions: {
          label: item.header,
          placeholder: item.header,
          options: optionsList
        }
      };
      this.fieldsFeatures.push(fieldConfig);
    }
  }

  addDateRange(element, params) {
    let defaultValue = null;
    if (params.get(element.code + '_from') !== null && params.get(element.code + '_to') !== null) {
      defaultValue = new TuiDayRange(TuiDay.normalizeParse(params.get(element.code + '_from'), 'yyyy/mm/dd'), TuiDay.normalizeParse(params.get(element.code + '_to'), 'yyyy/mm/dd'));
    }

    const fieldConfig: FormlyFieldConfig = {
      formControl: new FormControl(),
      defaultValue: defaultValue,
      key: element.code,
      type: 'date-range-picker',
      templateOptions: {
        label: element.header,
        placeholder: element.header
      }
    };

    this.fieldsFeatures.push(fieldConfig);
  }

  addTextInput(element, params) {
    let defaultValue = '';

    this.textFields
      .filter(field => element.code === field && params.get(field) !== null)
      .forEach(field => defaultValue = params.get(field));

    const fieldConfig: FormlyFieldConfig = {
      key: element.code,
      type: element.type === 'textarea' ? 'textarea' : 'input-filter-property',
      defaultValue: defaultValue,
      templateOptions: {
        label: element.header,
        placeholder: element.header
      }
    };
    this.fieldsFeatures.push(fieldConfig);
  }

  getOptionsList(item) {
    const items = item.values || item.featureValue;

    let optionsList = [];
    if (items?.length) {
      optionsList = this.compoApiService.naturalSort(items, 'value').map(value => {
        return {
          value: value.id,
          label: value.value,
          icon: value.icon ?? undefined,
          color: value.color ?? undefined,
        };
      });
    }
    return optionsList;
  }

  /**
   * Булев флаг `featureProduct` (`hasStock`, `actionProduct`, `recommended`) — чекбокс.
   *
   * НЕ мультиселект: бэк отдаёт такие поля как `{type: 'checkbox', featureValue: null}`,
   * значений у них нет и быть не может, поэтому список опций всегда пуст. Ровно это и
   * случилось после PIM-493, где метод переписали в `select-multi-filter-property` с
   * опциями из `item.values`: в фильтре каталога «В наличии», «Акционный товар» и
   * «Рекомендованный товар» превратились в выпадашки с «No items found». Мультиселект
   * там, ради которого правку делали, живёт в ветке `features` → `FeatureTypes.BOOLEAN`,
   * и тем же коммитом она была переведена на `addSelect` — у этого метода остался
   * единственный вызывающий, и он про флаги.
   *
   * Значение приходит из URL строкой (`?hasStock=true`), обратно уезжает через
   * `convertModelFeatures`, который выбрасывает `false` из query. Спека —
   * `list-filter.service.spec.ts`.
   */
  addCheckboxField(item, params) {
    const raw = params.get(item.id.toString());
    let defaultValue = false;

    if (raw !== null && raw !== '0') {
      try {
        defaultValue = JSON.parse(raw);
      } catch {
        defaultValue = !!raw;
      }
    }

    this.fieldsFeatures.push({
      key: item.id,
      type: 'checkbox',
      defaultValue: defaultValue,
      templateOptions: {
        label: item.header,
        hideLabel: true,
        placeholder: item.header
      }
    });
  }

  addIntervalField(item, params, filter: FeatureFilter) {
    let defaultValueStart = '';
    let defaultValueEnd = '';
    const values1 = params.get(item.id.toString());
    if (values1 !== null && values1 !== undefined) {
      const valuesList = values1.split(';');
      defaultValueStart = valuesList[0];
      defaultValueEnd = valuesList[1];
    }

    function getField(key: string, defaultValue: string, placeholder: string): FormlyFieldConfig {
      return {
        key: key,
        defaultValue: defaultValue,
        type: 'input',
        modelOptions: {
          updateOn: 'blur',
        },
        templateOptions: {
          type: 'number',
          min: filter.minMax[item.id].min,
          max: filter.minMax[item.id].max,
          placeholder: placeholder,
          keydown: (field: FormlyFieldConfig, event: KeyboardEvent) => {
            if (event.key === 'Enter') {
              field.formControl.setValue(
                (event.target as HTMLInputElement).value,
                {emitEvent: true, emitModelToViewChange: true}
              );
            }
          }
        }
      };
    }

    if (filter.minMax[item.id.toString()] !== undefined) {
      this.fieldsFeatures.push({
        key: item.id,
        wrappers: ['interval-wrapper'],
        templateOptions: {label: item.header},
        fieldGroup: [
          getField('start', defaultValueStart, 'от ' + filter.minMax[item.id].min),
          getField('stop', defaultValueEnd, 'до ' + filter.minMax[item.id].max),
        ],
      });
    }
  }

  addYesNoSelect(element, params) {
    let defaultValue = null;

    if (params.get(element.code) !== null && params.get(element.code) !== '0') {
      defaultValue = params.get(element.code);
    }

    const fieldConfig: FormlyFieldConfig = {
      key: element.code,
      type: 'select-filter-property',
      defaultValue: defaultValue,
      templateOptions: {
        simple: true,
        label: element.header,
        placeholder: translations.placeholderSelect,
        options: [{
          value: 'true',
          label: translations.yes
        }, {
          value: 'false',
          label: translations.no
        }],
        getDisplayValue: (field) => {
          return field.defaultValue === 'true' ? element.header : 'Не ' + element.header.toLowerCase();
        }
      }
    };
    this.fieldsFeatures.push(fieldConfig);
  }

  /**
   * Накопитель `modelFeaturesArr` — это «текущий query», и заполнять его надо ИЗ URL,
   * а не только пользовательскими правками.
   *
   * Без этого он начинался с `{}` каждый раз, когда состояние приехало не через клик:
   * прямая ссылка, перезагрузка, «назад», да и просто пересборка полей — она обнуляет
   * `modelFeatures` (ниже по `configFields`). А `submit()` пишет query целиком
   * (`queryParamsHandling: null`), то есть отправляет накопитель как ПОЛНЫЙ набор
   * параметров. Итог: сняли одну галочку из двух — вторая, о которой накопитель не
   * знал, исчезала вместе с ней.
   *
   * Спасал от этого только список `passParams`, куда точечно занесли `hasStock` и
   * прочее, — заплатка ровно на эту дыру, закрывавшая её для восьми ключей из всех.
   *
   * Берём те же `params`, из которых строятся `defaultValue` полей, — значит форма и
   * накопитель всегда описывают одно и то же состояние.
   */
  private seedModelFromParams(params: HttpParams): void {
    this.modelFeaturesArr = {};

    params.keys().forEach(key => {
      const value = params.get(key);
      if (value !== null && value !== '') {
        this.modelFeaturesArr[key] = value;
      }
    });
  }

  convertModelFeatures(): any {
    for (const [key, valueItem] of Object.entries(this.modelFeatures)) {

      if (valueItem === null) {
        delete this.modelFeaturesArr[key];
        delete this.modelFeaturesArr[key + '_from'];
        delete this.modelFeaturesArr[key + '_to'];

      } else if (typeof valueItem === 'object') {
        if (valueItem instanceof TuiDayRange) {
          this.modelFeaturesArr[key + '_from'] = valueItem.from.getFormattedDay('yyyy/mm/dd', '-');
          this.modelFeaturesArr[key + '_to'] = valueItem.to.getFormattedDay('yyyy/mm/dd', '-');
        }
        if (Array.isArray(valueItem)) {
          const ret = [];
          valueItem.forEach(value => {
            if (value) {
              if (value.value !== undefined) {
                ret.push(value.value === null ? 'null' : value.value);
              } else {
                ret.push(value);
              }
            }
          })
          if (ret.length > 0) {
            this.modelFeaturesArr[key] = ret.join(',');
          } else {
            delete this.modelFeaturesArr[key];
          }
        } else {
          this.modelFeaturesArr[key] = null;
          if ((valueItem['start'] !== undefined || valueItem['stop'] !== undefined)) {
            let urlValue = '';
            if (valueItem.hasOwnProperty('start') && valueItem['start'] !== null) {
              urlValue += valueItem['start'];
            }
            urlValue += ';';
            if (valueItem.hasOwnProperty('stop') && valueItem['stop'] !== null) {
              urlValue += valueItem['stop'];
            }
            if (urlValue !== ';') {
              this.modelFeaturesArr[key] = urlValue;
            }
          } else {
            delete this.modelFeaturesArr[key];
          }
          for (const key2 in valueItem) {
            if (valueItem.hasOwnProperty(key2) && key2 === 'value') {

              this.modelFeaturesArr[key] = valueItem[key2];
            }
          }

        }

      } else {
        if (valueItem !== undefined) {
          if ((this.yesNoSelectFilterKeys.includes(key) && (valueItem === 'false' || valueItem === false)) || valueItem !== 'false' && valueItem !== false && valueItem !== '' && valueItem !== '0' && valueItem !== 0) {
            this.modelFeaturesArr[key] = valueItem;
          } else {
            delete this.modelFeaturesArr[key];
          }
        }
      }
    }

    this.passParams.forEach(param => {
      if (this.route.snapshot.queryParamMap.get(param) && this.route.snapshot.queryParamMap.get(param) !== '0' && this.route.snapshot.queryParamMap.get(param) !== '') {
        this.modelFeaturesArr[param] = this.route.snapshot.queryParamMap.get(param);
      } else {
        delete this.modelFeaturesArr[param];
      }
    })

    if (this.getByKey('classId') && !this.modelFeaturesArr.classId) {
      for (const [key] of Object.entries(this.modelFeaturesArr)) {

        if (/^\d+(_width|_height|_depth)?$/.test(key)) {
          delete this.modelFeaturesArr[key];
        }
      }
    }

    return this.modelFeaturesArr;
  }

  reset() {
    this.ctnActiveFeatures$.next(0);
    this.modelFeatures = {};
    this.modelFeaturesArr = {};
    this.setFields([]);
  }

  addSelect(item, params: HttpParams) {
    let optionsList = this.getOptionsList(item);

    let defaultValue = this.getMultiSelectDefaultValue(params, item, optionsList);

    const fieldConfig: FormlyFieldConfig = {
      key: item.id,
      type: 'select-multi-filter-property',
      defaultValue: defaultValue,
      templateOptions: {
        label: item.header,
        placeholder: item.header,
        options: optionsList
      }
    };
    this.fieldsFeatures.push(fieldConfig);
  }

  getByKey(key: string) {
    return this.fieldsFeatures.find(x => x.key === key);
  }

  addSimpleSelect(element, params: HttpParams) {
    let optionsList = this.getOptionsList(element);
    let defaultValue = null;

    if (element.header == 'Шаблон' && this.environment.isOpenCabinet) {
      element.header = 'Тип изделия';
    }

    this.selectFields
      .filter(field => element.code === field && params.get(field) !== null)
      .forEach(field => {
        defaultValue = optionsList.filter(obj => {
          return obj.value === Number(params.get(field));
        });
      });

    const fieldConfig: FormlyFieldConfig = {
      key: element.code,
      type: 'select-filter-property',
      defaultValue: (defaultValue != null && defaultValue[0] !== undefined) ? defaultValue[0] : null,
      templateOptions: {
        label: element.header,
        placeholder: element.header,
        options: optionsList
      }
    };
    this.fieldsFeatures.push(fieldConfig);
  }

  private getMultiSelectDefaultValue(params, item, optionsList: { count: number; label: string; value: any }[]) {
    let defaultValue = [];
    if (params.get(item.id.toString()) !== null) {
      const values = params.get(item.id.toString()).split(',');
      optionsList.forEach(option => {
        if (values.includes(option.value ? option.value.toString() : null) || (option.value === null && values.includes("null"))) {
          defaultValue.push(option);
        }
      });
    }
    return defaultValue;
  }

}
